"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User } from "@/context/user-context";
import { toast } from "sonner";
import { useUpdateUser } from "./use-user-mutations";
import { useLocalization } from "@/context/localization-context";

interface PasswordChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: User | null;
}

export function PasswordChangeDialog({
  open,
  onOpenChange,
  user,
}: PasswordChangeDialogProps) {
  const { t } = useLocalization();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { updateUser, isUpdating: isLoading } = useUpdateUser();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      toast.error(t("users.password.empty"));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t("users.password.mismatch"));
      return;
    }

    if (!user) {
      toast.error(t("users.password.noUser"));
      return;
    }

    const updated = await updateUser(
      user._id,
      { psw: password },
      { success: t("users.password.updated"), error: t("users.password.updateFailed") },
    );
    if (updated) onOpenChange(false);
  };

  // Reset form when dialog opens/closes or user changes
  React.useEffect(() => {
    if (open) {
      setPassword("");
      setConfirmPassword("");
    }
  }, [open, user]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("users.actions.changePassword")}</DialogTitle>
          <DialogDescription>
            {t("users.password.description", { login: user?.login ?? "" })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="password">{t("users.password.newPassword")} *</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("users.password.newPasswordPlaceholder")}
                autoComplete="new-password"
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <Label htmlFor="confirm-password">{t("users.password.confirm")} *</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("users.password.confirmPlaceholder")}
                autoComplete="new-password"
                disabled={isLoading}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? t("users.form.saving")
                : t("users.actions.changePassword")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
